// Replaces Chonky's <FileList/>. We keep <FileBrowser> mounted (so
// ChonkyActions / fileActions / onFileAction / hotkeys still work exactly
// as they do in VFSBrowser.tsx today) but render every row ourselves,
// because FileList has no public column API and FileContextMenu is a
// right-click-only menu with no per-row trigger button.
//
// Selection state is owned by VFSBrowser and shared with the tile view via
// `useFileListSelection`, so switching between list and tile keeps the same
// selection. Menu options / row-action dispatch / icons come from the shared
// `fileListShared` module so both views stay in sync.

import { useState } from 'react';
import './CustomFilelist.scss';
import { Checkbox, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { FileNode } from '../../../../types/FileManagerTypes';
import dayjs from 'dayjs';
import { useFileListSelection } from '../../../../hooks/useFileListSelection';
import { useAuth } from '../../../../context/AuthContext';
import {
  FileOptionsPanel,
  InlineRenameInput,
  NewFolderInput,
  SelectAllCheckbox,
  dispatchRowAction,
  getFileOptions,
  getRowOptionsForFile,
  getFileTypeLabel,
  FileTypeIcon,
} from '../fileListShared';
import { EmptyState } from '../EmptyState/EmptyState';
import { formatBytes } from '../../constants';

function RowIcon({ file }: { file: FileNode }) {
  return <FileTypeIcon file={file} variant="list" className="cfl-icon" />;
}

export interface CustomFileListProps {
  /**
   * Matches Chonky's own `FileArray` prop type (`Nullable<FileData>[]`) --
   * null/undefined entries are filtered out before rendering (see the shared
   * `useFileListSelection.visibleFiles`) rather than assumed away.
   */
  files: (FileNode | null | undefined)[];
  /**
   * Same signature passed to <FullFileBrowser onFileAction={...}>. The 3-dot
   * menu dispatches through this exact same handler.
   */
  onFileAction: (data: any) => void;
  /** Fired on double-click for a folder row -- wire to handleFileOpen. */
  onOpenFolder: (file: FileNode) => void;
  /** Fired on double-click for a file row -- opens the details/preview modal. */
  onPreviewFile: (file: FileNode) => void;
  /** Persistent selection owned by VFSBrowser (shared with the tile view). */
  selection: string[];
  setSelection: (ids: string[]) => void;
  /** id of a row currently in "insert name" inline-rename mode. */
  editingId?: string | null;
  /** "Rename" was picked -- caller flips this row into inline-edit mode. */
  onRequestRename?: (file: FileNode) => void;
  /** Inline rename committed via Enter. Return a string to show an inline error. */
  onCommitName?: (file: FileNode, value: string) => void | string | Promise<void | string | null>;
  /** Esc or blur (click outside) in the rename input -- caller clears edit mode. */
  onCancelRename?: (file: FileNode) => void;
  /** True on the /files/trash route -- swaps the 3-dot menu to trash actions. */
  isTrash?: boolean;
  /**
   * When true, an inline folder creation row is prepended above data rows.
   * Wired by the footer add-folder flow.
   */
  creatingFolder?: boolean;
  /** Pre-filled default name for the temp-folder input (e.g. "New Folder (1)"). */
  newFolderDefaultName?: string;
  /** True while the create request is in flight -- disables the temp input. */
  submitting?: boolean;
  onCommitNewFolder?: (value: string) => void;
  onCancelNewFolder?: () => void;
  /** Current search term — used to determine which empty state to display. */
  searchTerm?: string;
  /** Whether a search is currently active — controls result-click behavior. */
  isSearchActive?: boolean;
  /** Whether filters are currently applied — takes precedence for the empty state. */
  isFilterActive?: boolean;
  /** Called when a search result row is selected — clears search and navigates. */
  onSelectSearchResult?: (file: FileNode) => void;
}

export function CustomFileList({
  files,
  onFileAction,
  onOpenFolder,
  onPreviewFile,
  selection,
  setSelection,
  editingId = null,
  onRequestRename,
  onCommitName,
  onCancelRename,
  isTrash = false,
  creatingFolder = false,
  newFolderDefaultName = 'New Folder',
  submitting = false,
  onCommitNewFolder,
  onCancelNewFolder,
  searchTerm = '',
  isSearchActive = false,
  isFilterActive = false,
  onSelectSearchResult,
}: CustomFileListProps) {
  const [dropdownOpenFileId, setDropdownOpenFileId] = useState<string | null>(null);
  const { canAccess } = useAuth();

  const { visibleFiles, isSelected, selectedCount, toggleSelection } = useFileListSelection({ files, selection, setSelection, onFileAction });

  const rowOptions = getFileOptions(isTrash, selectedCount > 1, canAccess);

  return (
    <div className="cfl-table" role="table" aria-label="Files">
      <div className="cfl-table-wrapper">
        <div className="cfl-row cfl-row--head" role="row">
          <div className="cfl-cell cfl-cell--checkbox">
            <SelectAllCheckbox files={files} selection={selection} setSelection={setSelection} onFileAction={onFileAction} />
          </div>
          <div className="cfl-cell cfl-cell--checkbox" />
          <div className="cfl-cell cfl-cell--name" role="columnheader">
            Name
          </div>
          <div className="cfl-cell" role="columnheader">
            Date
          </div>
          <div className="cfl-cell" role="columnheader">
            Username
          </div>
          <div className="cfl-cell" role="columnheader">
            File type
          </div>
          <div className="cfl-cell" role="columnheader">
            File size
          </div>
          <div className="cfl-cell cfl-cell--actions" />
        </div>
        {creatingFolder && (
          <div className="cfl-row cfl-row--new" role="row">
            <div className="cfl-cell cfl-cell--checkbox" />
            <div className="cfl-cell cfl-cell--checkbox">
              <img className="cfl-icon" src="/assets/folder-icon-list.svg" alt="new folder" />
            </div>
            <div className="cfl-cell cfl-cell--name">
              <NewFolderInput
                submitting={submitting}
                defaultName={newFolderDefaultName}
                creatingClassName="cfl-row__creating"
                onCommit={(value) => onCommitNewFolder?.(value)}
                onCancel={() => onCancelNewFolder?.()}
              />
            </div>
            <div className="cfl-cell" />
            <div className="cfl-cell" />
            <div className="cfl-cell" />
            <div className="cfl-cell" />
            <div className="cfl-cell cfl-cell--actions" />
          </div>
        )}
        {visibleFiles.length === 0 && !creatingFolder && (
          <EmptyState type={isFilterActive ? 'filter' : searchTerm.length > 0 ? 'search' : 'folder'} />
        )}
        {visibleFiles.map((file) => {
          const selected = isSelected(file.id);
          const editing = editingId === file.id;
          return (
            <div
              className={`cfl-row${selected ? ' is-selected' : ''}${editing ? ' is-editing' : ''}`}
              role="row"
              key={file.id}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (isSearchActive && onSelectSearchResult) {
                  onSelectSearchResult(file);
                } else {
                  file.isDir ? onOpenFolder(file) : onPreviewFile(file);
                }
              }}
            >
              <div className="cfl-cell cfl-cell--checkbox" onDoubleClick={(e) => e.stopPropagation()}>
                <Checkbox
                  className="custom-checkbox"
                  checked={selected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelection(file)}
                />
              </div>
              <div className="cfl-cell cfl-cell--checkbox">
                <RowIcon file={file} />
              </div>
              <div className="cfl-cell cfl-cell--name">
                {editing ? (
                  <InlineRenameInput file={file} onCommit={(f, value) => onCommitName?.(f, value)} onCancel={(f) => onCancelRename?.(f)} />
                ) : (
                  <span className="cfl-name">{file.name}</span>
                )}
              </div>

              <div className="cfl-cell">{dayjs((file as any).modDate).format('MMM DD, YYYY')}</div>
              <div className="cfl-cell">{(file as any).username}</div>
              <div className="cfl-cell">{file.isDir ? 'FILE FOLDER' : getFileTypeLabel(file.name)}</div>
              <div className="cfl-cell">{file.isDir ? '' : formatBytes(file.size)}</div>

              <div className="cfl-cell cfl-cell--actions">
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  open={dropdownOpenFileId === file.id}
                  onOpenChange={(open) => setDropdownOpenFileId(open ? file.id : null)}
                  dropdownRender={() => (
                    <FileOptionsPanel
                      options={getRowOptionsForFile(file, rowOptions)}
                      isMultiselect={selectedCount > 1}
                      onSelect={(actionId) =>
                        dispatchRowAction(
                          actionId,
                          file,
                          {
                            onFileAction,
                            onRequestRename,
                            onClose: () => setDropdownOpenFileId(null),
                          },
                          visibleFiles.filter((f) => isSelected(f.id)),
                        )
                      }
                    />
                  )}
                >
                  <div
                    className="cfl-dots-btn"
                    aria-label="File options"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    <MoreOutlined />
                  </div>
                </Dropdown>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
